(function (root) {
  'use strict';
  function createClient(env, config, state) {
    var routing = env.routing;
    var doc = env.document;
    var active = false;
    var measurementReady = false;
    var pending = null;
    var token = null;
    var remote = null;
    var observedForms = new WeakSet();
    var handledResponses = new WeakSet();
    var observer = null;
    var session = null;
    var withdrawalPromise = null;
    var withdrawalAttempts = 0;
    var refreshPending = null;
    var cartPending = null;
    var cartWrittenToken = null;
    var cartAttempts = 0;
    var variantIds = new Set((config.variantIds || []).map(String));
    function analyticsAllowed() {
      try { return env.window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; } catch (_) { return false; }
    }
    function registered() {
      return active && analyticsAllowed() && remote?.enabled === true && Date.parse(config.endsAt) > env.now() &&
        Date.parse(remote.endsAt) > env.now() && token && state.assignment?.arm === routing.actualArm(config);
    }
    function current() { return registered() && measurementReady; }
    async function request(path, body, method) {
      if (!analyticsAllowed()) throw new Error('consent_required');
      var controller = new AbortController();
      var timer = env.setTimeout(function () { controller.abort(); }, 5000);
      try {
        var response = await env.fetch(config.collector.replace(/\/$/, '') + path, {
          method:method || 'POST',mode:'cors',credentials:'omit',cache:'no-store',signal:controller.signal,
          headers:body ? {'Content-Type':'application/json'} : {},
          body:body ? JSON.stringify(body) : undefined
        });
        var data = await response.json();
        if (!response.ok) throw new Error(data?.error?.code || 'collector_unavailable');
        return data;
      } finally { env.clearTimeout(timer); }
    }
    function waitForPrivacy() {
      if (env.window.Shopify?.customerPrivacy) return Promise.resolve(analyticsAllowed());
      return new Promise(function (resolve) {
        var finished = false;
        var timer = env.setTimeout(function () { finish(false); }, 5000);
        function finish(value) { if (finished) return; finished = true; env.clearTimeout(timer); resolve(value); }
        try {
          if (typeof env.window.Shopify?.loadFeatures !== 'function') { finish(false); return; }
          env.window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.2'}], function (error) { finish(!error && analyticsAllowed()); });
        } catch (_) { finish(false); }
      });
    }
    function formMatches(form) {
      var id = form.querySelector('[name="id"]')?.value;
      return variantIds.has(String(id));
    }
    function removeToken(form) {
      form.querySelectorAll('[data-zenify-ab-token]').forEach(function (input) { input.remove(); });
    }
    function bindForm(form) {
      if (!current() || !formMatches(form)) { removeToken(form); return; }
      var input = form.querySelector('[data-zenify-ab-token]');
      if (!input) {
        input = doc.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[_zenify_ab_token]';
        input.setAttribute('data-zenify-ab-token', '');
        form.appendChild(input);
      }
      if (input.value !== token) input.value = token;
      if (!observedForms.has(form)) {
        observedForms.add(form);
        form.addEventListener('formdata', function (event) {
          if (current() && formMatches(form)) event.formData.set('properties[_zenify_ab_token]', token);
          else event.formData.delete('properties[_zenify_ab_token]');
        });
      }
    }
    function bindForms() {
      if (!doc?.querySelectorAll) return;
      doc.querySelectorAll('form[action*="/cart/add"]').forEach(bindForm);
    }
    function revoke() {
      active = false;
      measurementReady = false;
      token = null;
      bindForms();
      observer?.disconnect();
      observer = null;
    }
    async function flushPendingWithdrawal() {
      if (withdrawalPromise) return withdrawalPromise;
      var record = routing.read(env.storage,routing.key(config,'withdrawal_pending'));
      if (!record || record.experimentId !== config.experimentId || !record.token || withdrawalAttempts >= 3) return false;
      try { if (new URL(config.collector).protocol !== 'https:') return false; } catch (_) { return false; }
      revoke();
      withdrawalPromise = (async function () {
        while (withdrawalAttempts < 3) {
          withdrawalAttempts++;
          var controller = new AbortController();
          var timer = env.setTimeout(function () { controller.abort(); },5000);
          try {
            var response = await env.fetch(config.collector.replace(/\/$/, '') + '/v1/withdraw', {
              method:'POST',mode:'cors',credentials:'omit',keepalive:true,signal:controller.signal,
              headers:{'Content-Type':'application/json'},body:JSON.stringify({token:record.token})
            });
            if (response.ok) {
              try {
                env.storage.removeItem(routing.key(config,'withdrawal_pending'));
                env.storage.removeItem(routing.key(config,'token'));
              } catch (_) {}
              return true;
            }
          } catch (_) {} finally { env.clearTimeout(timer); }
          if (withdrawalAttempts < 3) await new Promise(function (resolve) { env.setTimeout(resolve,withdrawalAttempts === 1 ? 750 : 2000); });
        }
        return false;
      })().finally(function () { withdrawalPromise = null; });
      return withdrawalPromise;
    }
    async function withdraw() {
      var saved = routing.read(env.storage,routing.key(config,'token'));
      var storedAssignment = state?.assignment || routing.read(env.storage,routing.key(config,'assignment'));
      var withdrawalToken = token || (saved && storedAssignment && saved.visitorId === storedAssignment.visitorId ? saved.token : null);
      revoke();
      if (withdrawalToken && storedAssignment) {
        routing.write(env.storage,routing.key(config,'withdrawn'),{visitorId:storedAssignment.visitorId});
        routing.write(env.storage,routing.key(config,'withdrawal_pending'),{experimentId:config.experimentId,visitorId:storedAssignment.visitorId,token:withdrawalToken});
      }
      return flushPendingWithdrawal();
    }
    async function readRemote() {
      var settings = await request('/public/config', null, 'GET');
      if (settings.experimentId !== config.experimentId || settings.targetProductId !== String(config.targetProductId) ||
          settings.campaign !== config.campaign || settings.bView !== config.bView) throw new Error('configuration_mismatch');
      remote = settings;
      routing.write(env.storage, routing.key(config, 'remote'), {experimentId:config.experimentId,enabled:settings.enabled === true,checkedAt:env.now()});
      if (settings.enabled !== true || !Number.isFinite(Date.parse(settings.endsAt)) || Date.parse(settings.endsAt) <= env.now()) { revoke(); throw new Error('experiment_disabled'); }
      return settings;
    }
    function getSession() {
      var existing = routing.read(env.sessionStorage, routing.key(config, 'session'));
      var now = env.now();
      if (!existing || now - existing.lastSeen > 1800000) existing = {id:routing.uuid(env.crypto),lastSeen:now};
      existing.lastSeen = now;
      if (!routing.write(env.sessionStorage, routing.key(config, 'session'), existing)) throw new Error('session_storage_unavailable');
      return existing;
    }
    function visible() {
      if (doc.visibilityState !== 'visible') return false;
      var el = doc.querySelector(routing.actualArm(config) === 'B' ? 'zenify-ritual-landing' : '#MainContent');
      if (!el) return false;
      var box = el.getBoundingClientRect();
      if (!box.width || !box.height) return false;
      var style = env.window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    }
    var exposurePending = null;
    var exposureAttempts = 0;
    function measurementConfirmed() {
      if (!registered()) return;
      measurementReady = true;
      bindForms();
      if (env.window.MutationObserver && !observer) {
        observer = new env.window.MutationObserver(bindForms);
        observer.observe(doc.getElementById('MainContent') || doc.body, {childList:true,subtree:true});
      }
      void setAuxiliaryCartAttribute();
    }
    async function expose() {
      if (!registered() || !session || !visible()) return false;
      if (exposurePending) return exposurePending;
      var name = routing.key(config, 'exposure:' + state.assignment.visitorId + ':' + session.id);
      var record = routing.read(env.sessionStorage, name);
      if (record?.sent) { if (!measurementReady) measurementConfirmed(); return true; }
      if (exposureAttempts >= 3) return false;
      record = record || {eventId:routing.uuid(env.crypto),sent:false};
      if (!routing.write(env.sessionStorage, name, record)) return false;
      exposurePending = (async function () {
        while (exposureAttempts < 3 && registered() && visible()) {
          exposureAttempts++;
          try {
            await request('/v1/exposure', {token:token,eventId:record.eventId,sessionId:session.id});
            routing.write(env.sessionStorage, name, {eventId:record.eventId,sent:true});
            measurementConfirmed();
            return true;
          } catch (_) {
            if (exposureAttempts >= 3 || !registered()) return false;
            await new Promise(function (resolve) { env.setTimeout(resolve, exposureAttempts === 1 ? 750 : 2000); });
          }
        }
        return false;
      })().finally(function () { exposurePending = null; });
      return exposurePending;
    }
    function propertiesForItem(item) {
      if (!current() || !variantIds.has(String(item.id))) return item;
      return Object.assign({}, item, {properties:Object.assign({}, item.properties || {}, {_zenify_ab_token:token})});
    }
    async function recordAddToCart(response, variantId) {
      if (!current() || !session || !response || typeof response !== 'object' || response.status || !variantIds.has(String(variantId))) return false;
      if (handledResponses.has(response)) return false;
      var lines = Array.isArray(response.items) ? response.items : [response];
      var line = lines.find(function (item) { return String(item.variant_id || item.id) === String(variantId) && item.quantity > 0 && item.properties?._zenify_ab_token === token; });
      if (!line) return false;
      handledResponses.add(response);
      void setAuxiliaryCartAttribute();
      try {
        await request('/v1/event', {token:token,eventId:routing.uuid(env.crypto),sessionId:session.id,type:'add_to_cart'});
        return true;
      } catch (_) { return false; }
    }
    async function setAuxiliaryCartAttribute() {
      if (!current()) return false;
      if (cartWrittenToken === token) return true;
      if (cartPending) return cartPending;
      if (cartAttempts >= 3) return false;
      var writtenToken = token;
      cartAttempts++;
      cartPending = (async function () {
        var controller = new AbortController();
        var timer = env.setTimeout(function () { controller.abort(); }, 5000);
        try {
          var route = env.window.Shopify?.routes?.root || '/';
          var response = await env.fetch(route + 'cart/update.js', {
            method:'POST',credentials:'same-origin',keepalive:true,signal:controller.signal,headers:{'Content-Type':'application/json'},
            body:JSON.stringify({attributes:{__zenify_ab_token:writtenToken}})
          });
          if (response.ok) cartWrittenToken = writtenToken;
          return response.ok;
        } catch (_) { return false; }
        finally { env.clearTimeout(timer); }
      })().finally(function () { cartPending = null; });
      return cartPending;
    }
    async function start() {
      if (pending) return pending;
      await flushPendingWithdrawal();
      if (pending) return pending;
      var storedAssignment = state?.assignment || routing.read(env.storage,routing.key(config,'assignment'));
      var withdrawn = routing.read(env.storage,routing.key(config,'withdrawn'));
      if (storedAssignment && withdrawn?.visitorId === storedAssignment.visitorId) return false;
      if ((!state?.eligible && !['await_consent','await_config','remote_disabled'].includes(state?.reason)) || state.redirected || !routing.usable(config, env.now())) return false;
      pending = (async function () {
        if (!await waitForPrivacy()) { if (env.window.Shopify?.customerPrivacy) void withdraw(); else revoke(); return false; }
        await readRemote();
        if (config.singleDocument && !routing.ensureAssignment(config,Object.assign({},env,{analyticsAllowed:analyticsAllowed}),state)) { revoke(); return false; }
        if (!config.singleDocument && (state.reason === 'await_consent' || state.reason === 'await_config')) {
          state = routing.run(config,Object.assign({},env,{analyticsAllowed:analyticsAllowed}));
          env.window.ZenifyABState = state;
        }
        if (!state.eligible || state.redirected || state.assignment?.arm !== routing.actualArm(config)) { revoke(); return false; }
        var assignment = await request('/v1/assignment', {experimentId:config.experimentId,visitorId:state.assignment.visitorId,arm:state.assignment.arm});
        if (assignment.experimentId !== config.experimentId || !['A','B'].includes(assignment.arm) || typeof assignment.assignmentToken !== 'string' || !assignment.assignmentToken || !Number.isFinite(Date.parse(assignment.expiresAt)) || Date.parse(assignment.expiresAt) <= env.now() || Date.parse(assignment.expiresAt) > Math.min(Date.parse(config.endsAt),Date.parse(remote.endsAt))) throw new Error('invalid_assignment');
        if (!analyticsAllowed()) { revoke(); return false; }
        state.assignment.arm = assignment.arm;
        state.assignment.expiresAt = assignment.expiresAt;
        if (!routing.write(env.storage, routing.key(config, 'assignment'), state.assignment)) throw new Error('storage_unavailable');
        if (assignment.arm !== routing.actualArm(config)) { revoke(); return false; }
        token = assignment.assignmentToken;
        if (!routing.write(env.storage, routing.key(config, 'token'), {token:token,visitorId:state.assignment.visitorId,expiresAt:assignment.expiresAt})) throw new Error('storage_unavailable');
        session = getSession();
        active = true;
        await new Promise(function (resolve) { env.window.requestAnimationFrame(function () { env.window.requestAnimationFrame(resolve); }); });
        return expose();
      })().catch(function () { revoke(); return false; }).finally(function () { pending = null; });
      return pending;
    }
    async function refresh() {
      if (pending) return pending;
      if (refreshPending) return refreshPending;
      if (exposurePending) return exposurePending;
      refreshPending = (async function () {
        if (!active) { exposureAttempts = 0; return start(); }
        if (!analyticsAllowed()) { void withdraw(); return false; }
        try {
          await readRemote();
          if (!measurementReady) exposureAttempts = 0;
          var exposed = await expose();
          if (current()) void setAuxiliaryCartAttribute();
          return exposed;
        } catch (_) { revoke(); return false; }
      })().finally(function () { refreshPending = null; });
      return refreshPending;
    }
    function hook() {
      var recoveryTimer = null;
      var recoveryAttempts = 0;
      function maintain() {
        if (doc.visibilityState !== 'visible') return;
        void refresh().then(function (ready) {
          if (ready || recoveryTimer || recoveryAttempts >= 3 || !routing.usable(config, env.now())) return;
          if (!state?.eligible && !['await_consent','await_config'].includes(state?.reason)) return;
          if ((env.window.Shopify?.customerPrivacy && !analyticsAllowed()) || remote?.enabled === false) return;
          var delay = [1000, 3000, 10000][recoveryAttempts++];
          recoveryTimer = env.setTimeout(function () { recoveryTimer = null; maintain(); }, delay);
        });
      }
      doc.addEventListener('visitorConsentCollected', function () { if (analyticsAllowed()) maintain(); else void withdraw(); });
      doc.addEventListener('visibilitychange', maintain);
      env.window.addEventListener?.('online', maintain);
      env.window.addEventListener?.('pageshow', maintain);
      doc.addEventListener('change', bindForms);
      function afterPaint() {
        env.window.requestAnimationFrame(function () {
          env.window.requestAnimationFrame(maintain);
        });
      }
      if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', afterPaint, {once:true});
      else afterPaint();
      env.window.setInterval(maintain, 60000);
    }
    return {start:start,expose:expose,refresh:refresh,revoke:revoke,withdraw:withdraw,flushPendingWithdrawal:flushPendingWithdrawal,hook:hook,bindForm:bindForm,propertiesForItem:propertiesForItem,recordAddToCart:recordAddToCart,isActive:current};
  }
  root.ZenifyABClientFactory = createClient;
  if (root.document && root.ZenifyABSettings && root.ZenifyABRouting) {
    try {
      var client = createClient({window:root,document:root.document,location:root.location,routing:root.ZenifyABRouting,storage:root.localStorage,sessionStorage:root.sessionStorage,fetch:root.fetch.bind(root),crypto:root.crypto,now:Date.now,setTimeout:root.setTimeout.bind(root),clearTimeout:root.clearTimeout.bind(root)},root.ZenifyABSettings,root.ZenifyABState);
      root.ZenifyABClient = client;
      root.ZenifyAB = {propertiesForItem:client.propertiesForItem,recordAddToCart:client.recordAddToCart};
      client.hook();
    } catch (_) { root.ZenifyAB = {propertiesForItem:function(item){return item;},recordAddToCart:function(){return false;}}; }
  }
})(typeof window !== 'undefined' ? window : globalThis);
